/*
 * @Descripttion: 场景价格区间校验
 * @Date: 2026-07-02 10:00:00
 */

import type { PriceRangeTuple } from '../data-source/types';
import { MAX_PRICE, MIN_PRICE, PRICE_DECIMALS } from '../constants';

export type RangeError = {
  min: string;
  max: string;
};

/** 最多两位小数 */
export const PRICE_DECIMAL_REG = new RegExp(
  `^\\d+(\\.\\d{1,${PRICE_DECIMALS}})?$`
);

/**
 * 价格输入展示（最多两位小数）
 */
export const formatPriceInputText = (value?: number | null): string => {
  if (value == null || !Number.isFinite(value)) {
    return '';
  }
  return String(Number(value.toFixed(PRICE_DECIMALS)));
};

/**
 * 硬性校验：必填、数字、小数位、0~1000、下限≤上限
 * 不校验是否超出平台区间
 */
export const validatePriceRange = (
  min: string,
  max: string,
  _platform?: PriceRangeTuple,
  required = true
): RangeError => {
  const err: RangeError = { min: '', max: '' };
  const loText = min.trim();
  const hiText = max.trim();
  const lo = Number(loText);
  const hi = Number(hiText);

  if (!required) {
    return err;
  }

  if (loText === '') {
    err.min = '下限必填';
  } else if (!Number.isFinite(lo)) {
    err.min = '请输入数字';
  } else if (!PRICE_DECIMAL_REG.test(loText)) {
    err.min = '最多保留两位小数';
  } else if (lo < MIN_PRICE) {
    err.min = `不能低于 ${MIN_PRICE}`;
  } else if (lo > MAX_PRICE) {
    err.min = `不能高于 ${MAX_PRICE}`;
  }

  if (hiText === '') {
    err.max = '上限必填';
  } else if (!Number.isFinite(hi)) {
    err.max = '请输入数字';
  } else if (!PRICE_DECIMAL_REG.test(hiText)) {
    err.max = '最多保留两位小数';
  } else if (hi < MIN_PRICE) {
    err.max = `不能低于 ${MIN_PRICE}`;
  } else if (hi > MAX_PRICE) {
    err.max = `不能高于 ${MAX_PRICE}`;
  }

  // 数字区间必须低到高
  if (!err.min && !err.max && lo > hi) {
    err.min = '下限不能大于上限';
    err.max = '上限不能小于下限';
  }

  return err;
};

/**
 * 是否超出平台价格区间（仅提示，不阻断输入）
 */
export const isOutOfPlatformRange = (
  min: string,
  max: string,
  platform: PriceRangeTuple
): boolean => {
  const lo = Number(min.trim());
  const hi = Number(max.trim());
  if (!Number.isFinite(lo) || !Number.isFinite(hi)) {
    return false;
  }
  return lo < platform[0] || hi > platform[1];
};

/**
 * 超出平台区间时的提示文案（可展示在输入下方）
 */
export const getPlatformRangeWarning = (
  min: string,
  max: string,
  platform: PriceRangeTuple
): RangeError => {
  const warn: RangeError = { min: '', max: '' };
  const lo = Number(min.trim());
  const hi = Number(max.trim());

  if (Number.isFinite(lo) && lo < platform[0]) {
    warn.min = '当前价格超过平台区间';
  }
  if (Number.isFinite(hi) && hi > platform[1]) {
    warn.max = '当前价格超过平台区间';
  }

  return warn;
};

export const hasRangeError = (err: RangeError) => !!(err.min || err.max);

/**
 * 格式化价格区间展示
 */
export const formatPriceRange = (
  lower?: number | null,
  upper?: number | null
): string => {
  if (lower == null || upper == null) {
    return '-';
  }
  return `${formatPriceInputText(lower)} ~ ${formatPriceInputText(upper)} 元`;
};

/**
 * 获取生效中的工业价（未配置时回退平台价）
 */
export const getEffectiveIndustryRange = (
  platform: PriceRangeTuple,
  industryLower?: number | null,
  industryUpper?: number | null
): PriceRangeTuple => {
  if (industryLower != null && industryUpper != null) {
    return [industryLower, industryUpper];
  }
  return platform;
};
